import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Loader2, Crown, CalendarClock, Gift, Copy, Check, Receipt, XCircle, RefreshCw, Users } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Account() {
  const { user, refreshUser } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const LOC = lang === "en" ? "en-US" : "fr-FR";
  const [sub, setSub] = useState(null);
  const [ref, setRef] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [receipt, setReceipt] = useState(null);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d) ? "—" : d.toLocaleDateString(LOC, { day: "numeric", month: "long", year: "numeric" });
  };
  const daysLeft = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d)) return null;
    return Math.max(0, Math.ceil((d.getTime() - Date.now()) / 86400000));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([api.get("/payments/subscription"), api.get("/prefs/referral")]);
      setSub(s.data);
      setRef(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cancel = async () => {
    if (!window.confirm(t("acc.confirmCancel"))) return;
    setBusy(true);
    try {
      await api.post("/payments/cancel");
      toast.success(t("acc.canceledOk"));
      await load(); await refreshUser?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const reactivate = async () => {
    setBusy(true);
    try {
      await api.post("/payments/reactivate");
      toast.success(t("acc.reactivatedOk"));
      await load(); await refreshUser?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const refLink = ref ? `${window.location.origin}/register?ref=${ref.code}` : "";
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(refLink); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { toast.error(t("acc.copyFail")); }
  };

  if (loading) return <div className="flex items-center gap-2 p-6 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}</div>;

  const plan = sub?.plan || "free";
  const isPaid = plan !== "free" && !!sub?.plan_expires_at;
  const left = daysLeft(sub?.plan_expires_at);

  return (
    <div className="space-y-6" data-testid="account-page">
      <PageHeader title={t("acc.title")} subtitle={t("acc.subtitle")} icon={CreditCard} />

      {/* Abonnement */}
      <div className="rounded-xl border border-slate-800 bg-[#0E1118] p-5" data-testid="subscription-card">
        <div className="mb-4 flex items-center gap-2"><Crown className="h-4 w-4 text-amber-400" /><h3 className="font-heading text-sm font-bold text-white">{t("acc.subscription")}</h3></div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <div className="text-xs text-slate-500">{t("acc.currentPlan")}</div>
            <div className="mt-1 flex items-center gap-2">
              <span className={`rounded px-2 py-0.5 text-sm font-bold ${plan === "premium" ? "bg-amber-500/15 text-amber-300" : plan === "basic" ? "bg-blue-500/15 text-blue-300" : "bg-slate-700/40 text-slate-400"}`}>{plan === "free" ? t("acc.free") : t(`plan.${plan}`)}</span>
              {sub?.is_trial && <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-300">{t("acc.trial")}</span>}
              {sub?.canceled && <span className="rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-red-300">{t("acc.canceled")}</span>}
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500">{sub?.canceled ? t("acc.accessUntil") : t("acc.expiration")}</div>
            <div className="mt-1 flex items-center gap-1.5 text-sm text-slate-200"><CalendarClock className="h-3.5 w-3.5 text-slate-500" /> {fmtDate(sub?.plan_expires_at)}</div>
            {left != null && isPaid && <div className="mt-0.5 text-[11px] text-slate-500">{left} {t("acc.daysLeft")}</div>}
          </div>
          <div className="flex items-end justify-start sm:justify-end">
            {plan === "free" && (
              <button data-testid="account-upgrade-btn" onClick={() => navigate("/tarifs")} className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2 text-sm font-bold text-black hover:opacity-90">
                <Crown className="h-4 w-4" /> {t("acc.upgrade")}
              </button>
            )}
            {isPaid && !sub?.canceled && (
              <button data-testid="cancel-sub-btn" onClick={cancel} disabled={busy} className="flex items-center gap-2 rounded-lg border border-red-800/60 bg-red-500/10 px-4 py-2 text-sm font-semibold text-red-300 hover:bg-red-500/20 disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />} {t("acc.cancelSub")}
              </button>
            )}
            {isPaid && sub?.canceled && (
              <button data-testid="reactivate-sub-btn" onClick={reactivate} disabled={busy} className="flex items-center gap-2 rounded-lg border border-emerald-800/60 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {t("acc.reactivate")}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Parrainage */}
      <div className="rounded-xl border border-slate-800 bg-[#0E1118] p-5" data-testid="referral-card">
        <div className="mb-3 flex items-center gap-2"><Gift className="h-4 w-4 text-emerald-400" /><h3 className="font-heading text-sm font-bold text-white">{t("acc.referral")}</h3></div>
        <p className="mb-3 text-sm text-slate-400">{t("acc.referralIntro")} <span className="font-semibold text-emerald-300">{ref?.reward_days || 30} {t("acc.referralDays")}</span>.</p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input readOnly value={refLink} data-testid="referral-link" className="flex-1 rounded-lg border border-slate-700 bg-[#11141A] px-3 py-2 text-sm text-slate-300 outline-none" />
          <button onClick={copyLink} data-testid="copy-referral-btn" className="flex items-center justify-center gap-2 rounded-lg bg-slate-700 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-600">
            {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />} {copied ? t("acc.copied") : t("acc.copy")}
          </button>
        </div>
        <div className="mt-4 flex gap-6">
          <div className="flex items-center gap-2 text-sm text-slate-300"><Users className="h-4 w-4 text-slate-500" /> {ref?.referred_count ?? 0} {t("acc.referrals")}</div>
          <div className="flex items-center gap-2 text-sm text-slate-300"><Crown className="h-4 w-4 text-amber-400" /> {ref?.converted_count ?? 0} {t("acc.converted")}</div>
        </div>
      </div>

      {/* Reçus */}
      <div className="rounded-xl border border-slate-800 bg-[#0E1118] p-5" data-testid="receipts-card">
        <div className="mb-3 flex items-center gap-2"><Receipt className="h-4 w-4 text-blue-400" /><h3 className="font-heading text-sm font-bold text-white">{t("acc.receipts")}</h3></div>
        {(!sub?.receipts || sub.receipts.length === 0) ? (
          <p className="text-sm text-slate-500">{t("acc.noReceipts")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-slate-500">
                <tr><th className="py-2 text-left">{t("acc.date")}</th><th className="py-2 text-left">{t("acc.plan")}</th><th className="py-2 text-right">{t("acc.amount")}</th><th className="py-2 text-right">{t("acc.receipt")}</th></tr>
              </thead>
              <tbody>
                {sub.receipts.map((tx) => (
                  <tr key={tx.session_id} className="border-t border-slate-800" data-testid="receipt-row">
                    <td className="py-2.5 text-slate-300">{fmtDate(tx.paid_at || tx.created_at)}</td>
                    <td className="py-2.5 text-slate-300">{t(`plan.${tx.plan_id}`)} · {tx.interval === "annual" ? t("acc.annual") : t("acc.monthly")}</td>
                    <td className="py-2.5 text-right font-mono text-slate-200">${Number(tx.amount).toFixed(2)}</td>
                    <td className="py-2.5 text-right">
                      <button onClick={() => setReceipt(tx)} data-testid="view-receipt-btn" className="rounded border border-slate-700 px-2.5 py-1 text-xs text-slate-300 hover:bg-slate-800">{t("acc.view")}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!receipt} onOpenChange={(o) => !o && setReceipt(null)}>
        <DialogContent className="max-w-sm border-slate-800 bg-[#12151C]" data-testid="receipt-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t("acc.receiptTitle")}</DialogTitle></DialogHeader>
          {receipt && (
            <div className="space-y-2 text-sm text-slate-300">
              <div className="flex justify-between"><span className="text-slate-500">{t("acc.date")}</span><span>{fmtDate(receipt.paid_at || receipt.created_at)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">{t("acc.plan")}</span><span>{t(`plan.${receipt.plan_id}`)} ({receipt.interval === "annual" ? t("acc.annual") : t("acc.monthly")})</span></div>
              {receipt.promo_code && <div className="flex justify-between"><span className="text-slate-500">{t("acc.promoCode")}</span><span className="text-emerald-300">{receipt.promo_code}</span></div>}
              <div className="flex justify-between"><span className="text-slate-500">{t("acc.txRef")}</span><span className="font-mono text-[11px]">{receipt.session_id?.slice(0, 22)}…</span></div>
              <div className="mt-2 flex justify-between border-t border-slate-800 pt-2 text-base font-bold text-white"><span>{t("acc.total")}</span><span>${Number(receipt.amount).toFixed(2)} USD</span></div>
              <button onClick={() => window.print()} className="mt-3 w-full rounded-lg bg-blue-600 py-2 text-sm font-semibold text-white hover:bg-blue-500">{t("acc.print")}</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
